import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  NgZone,
  OnDestroy,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { environment } from '@env/environment';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-api-js';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** Loads the Cloudflare Turnstile script once per page (shared with the contact form's script tag id). */
export function loadTurnstileScript(doc: Document = document): Promise<void> {
  const g = globalThis as any;
  if (g.turnstile !== undefined) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existing = doc.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? doc.createElement('script');
    const onLoad = (): void => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      resolve();
    };
    const onError = (): void => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      script.remove();
      reject(new Error('Turnstile script failed'));
    };
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);

    if (!existing) {
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      doc.head.appendChild(script);
    }
  });
}

/**
 * Cloudflare Turnstile bot check for the register-organization form. Same site key as the contact form;
 * the API verifies the token server-side (POST /public/client/register/start).
 * Emits the token when solved and null when it expires or errors.
 */
@Component({
  selector: 'app-turnstile-widget',
  imports: [CommonModule, MatButtonModule],
  template: `
    <div class="turnstile-wrapper">
      <div *ngIf="loadError" class="turnstile-message turnstile-error">
        <span>The security check couldn't load. Check your connection or VPN, then try again.</span>
        <button mat-button type="button" color="primary" (click)="retry()">Retry security check</button>
      </div>
      <div *ngIf="!ready && !loadError" class="turnstile-message">Loading security check…</div>
      <div #container class="turnstile-container"></div>
    </div>
  `,
  styles: [`
    .turnstile-wrapper { margin: 8px 0 16px; }
    .turnstile-container { min-height: 65px; }
    .turnstile-message { font-size: 0.875rem; color: #6b7280; margin-bottom: 8px; }
    .turnstile-error { color: #b91c1c; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  `]
})
export class TurnstileWidgetComponent implements AfterViewInit, OnDestroy {
  @Output() tokenChange = new EventEmitter<string | null>();
  @ViewChild('container', { static: true }) container!: ElementRef<HTMLElement>;

  ready = false;
  loadError = false;
  private widgetId: string | null = null;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    const api = (globalThis as any).turnstile;
    if (api !== undefined && this.widgetId !== null) {
      try {
        api.remove(this.widgetId);
      } catch {
        // widget already gone
      }
    }
    this.widgetId = null;
  }

  /** Clears the current token so a fresh challenge is required (tokens are single-use). */
  reset(): void {
    const api = (globalThis as any).turnstile;
    this.tokenChange.emit(null);
    if (api !== undefined && this.widgetId !== null) {
      try {
        api.reset(this.widgetId);
      } catch {
        this.retry();
      }
    }
  }

  retry(): void {
    this.ngOnDestroy();
    this.container.nativeElement.replaceChildren();
    this.ready = false;
    this.loadError = false;
    this.tokenChange.emit(null);
    this.load();
  }

  private load(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    loadTurnstileScript()
      .then(() => this.render())
      .catch(() => this.fail());
  }

  private render(): void {
    const api = (globalThis as any).turnstile;
    if (api === undefined || this.widgetId !== null) {
      if (api === undefined) {
        this.fail();
      }
      return;
    }
    try {
      const compact = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 576px)').matches;
      this.widgetId = api.render(this.container.nativeElement, {
        sitekey: environment.turnstileSiteKey,
        action: 'register_organization',
        size: compact ? 'compact' : 'normal',
        theme: 'light',
        callback: (token: string) => this.emit(token),
        'expired-callback': () => this.emit(null),
        'error-callback': () => this.emit(null)
      });
      this.ready = true;
      this.loadError = false;
      this.cdr.markForCheck();
    } catch {
      this.fail();
    }
  }

  private emit(token: string | null): void {
    this.zone.run(() => {
      this.tokenChange.emit(token);
      this.cdr.markForCheck();
    });
  }

  private fail(): void {
    this.widgetId = null;
    this.ready = false;
    this.loadError = true;
    this.cdr.markForCheck();
  }
}
